/**
 * Configuracoes Comerciais — /comercial/configuracoes (Bloco B Fase 4).
 *
 * Gestor com `comercial:configurar` ajusta:
 *   - Limites de dias para status temporal (Em aberto / Quase perdido / Perdido)
 *   - Tipo de contagem (corridos ou uteis)
 *   - Alertas automaticos ativos/desativos
 *
 * Mudancas sao retroativas: status_temporal eh computed em runtime,
 * todos os orcamentos sao reclassificados imediatamente sem migration.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiAlertCircle, FiCheck, FiSave } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarConfiguracao,
  listarConfiguracoes,
} from "../../../services/comercialConfig";
import {
  FATOR_LUCRO_PADRAO_POR_TIPO,
  TIPOS_COTACAO,
} from "../../../services/cotacoes";
import { listarCategorias, listarContas } from "../../../services/financeiro";
// Tarefa F2 — CRUD de formas de pagamento (condicoes + parcelas por evento).
import FormasPagamentoSection from "./FormasPagamento";
import InputDinheiro from "../../../components/InputDinheiro";
import { getSugestaoK } from "../../../services/motorPreco";
import { PageContainer } from "../../../styles/shared";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Field,
  FieldRow,
  FieldLabel,
  FieldValue,
  Form,
  FormFooter,
  FormGrid,
  InfoMsg,
  InlineHint,
  Input,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
  WarningMsg,
} from "../../Orcamentos/styles";

function fmtBRL(v) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function ConfiguracoesComercial() {
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [configs, setConfigs] = useState([]);
  // Opções para os selects de dinheiro automático (categorias RECEITA/DESPESA
  // e contas): sem elas o gestor não tem como ligar comissão/recebimento.
  const [catsReceita, setCatsReceita] = useState([]);
  const [catsDespesa, setCatsDespesa] = useState([]);
  const [contas, setContas] = useState([]);
  const [editando, setEditando] = useState({});
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState({});
  const [erros, setErros] = useState({});
  const [sucessos, setSucessos] = useState({});

  const podeEditar = isMaster || hasAllPermissions(["comercial:configurar"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await listarConfiguracoes(request);
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setConfigs(items);
      // Inicializa estado local de cada config
      const inicial = {};
      items.forEach((c) => {
        inicial[c.id] = {
          dias_em_aberto_max: c.dias_em_aberto_max,
          dias_quase_perdido_max: c.dias_quase_perdido_max,
          contagem: c.contagem,
          alertas_ativos: c.alertas_ativos,
          // Bloco E.2 v2 (ADR-0019)
          custo_operacional_padrao_percentual: c.custo_operacional_padrao_percentual ?? "0",
          margem_lucro_padrao_percentual: c.margem_lucro_padrao_percentual ?? "30",
          desconto_max_sem_aprovacao_percentual: c.desconto_max_sem_aprovacao_percentual ?? "10",
          // Painel Comercial (ADR-0079): a meta única e o alvo de resposta.
          meta_mensal_faturamento:
            c.meta_mensal_faturamento != null ? Number(c.meta_mensal_faturamento) : null,
          alvo_tempo_resposta_horas: c.alvo_tempo_resposta_horas ?? "4",
          // Contato que sai na proposta e no contrato — nunca mais o
          // e-mail de login de quem criou o orçamento.
          email_comercial: c.email_comercial ?? "",
          telefone_comercial: c.telefone_comercial ?? "",
          // Motor divisor (ADR-0050) — "" = nao configurado (null no backend)
          motor_preco_padrao: c.motor_preco_padrao || "CASCATA",
          k_cobertura_padrao_percentual: c.k_cobertura_padrao_percentual ?? "",
          margem_liquida_alvo_padrao_percentual: c.margem_liquida_alvo_padrao_percentual ?? "",
          imposto_venda_padrao_percentual: c.imposto_venda_padrao_percentual ?? "",
          fatores_terceiro_padrao: c.fatores_terceiro_padrao || {},
          // Auditoria 2026-08-07 (CRIT-6/CRIT-7): estes 5 campos existem no
          // backend desde sempre e NENHUMA tela os editava — por isso a
          // comissao automatica nunca disparou e o Contas a Receber ficou
          // zerado na historia do sistema.
          lancar_comissao_automaticamente: Boolean(c.lancar_comissao_automaticamente),
          categoria_lancamento_comissao: c.categoria_lancamento_comissao || "",
          dias_apos_entrega_para_pagar_comissao:
            c.dias_apos_entrega_para_pagar_comissao ?? 0,
          categoria_lancamento_recebimento: c.categoria_lancamento_recebimento || "",
          conta_recebimento_padrao: c.conta_recebimento_padrao || "",
        };
      });
      setEditando(inicial);
      try {
        const [rec, desp, cts] = await Promise.all([
          listarCategorias(request, { tipo: "RECEITA" }),
          listarCategorias(request, { tipo: "DESPESA" }),
          listarContas(request, { ativo: true }),
        ]);
        const lista = (p) => (Array.isArray(p) ? p : p?.results || []);
        setCatsReceita(lista(rec));
        setCatsDespesa(lista(desp));
        setContas(lista(cts));
      } catch {
        /* selects vazios não impedem editar o resto da configuração */
      }
    } catch (err) {
      setErros({ __global__: err?.message || "Erro ao carregar configurações." });
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const setCampo = (configId, campo, valor) => {
    setEditando((prev) => ({
      ...prev,
      [configId]: { ...prev[configId], [campo]: valor },
    }));
  };

  // ADR-0050: sugestao de k = OE/TVC dos meses fechados (financeiro), por
  // unidade. Sem `financeiro:view` o backend responde 403 — mostramos o
  // motivo em vez do breakdown.
  const [sugestoesK, setSugestoesK] = useState({});
  const [buscandoK, setBuscandoK] = useState({});

  const sugerirK = async (config) => {
    setBuscandoK((p) => ({ ...p, [config.id]: true }));
    try {
      const payload = await getSugestaoK(
        request,
        { unidade: config.business_unit },
        { silentError: true },
      );
      setSugestoesK((p) => ({ ...p, [config.id]: payload }));
    } catch (err) {
      setSugestoesK((p) => ({
        ...p,
        [config.id]: {
          erro: err?.status === 403
            ? "Sem permissão para consultar o financeiro (financeiro:view)."
            : err?.message || "Não foi possível calcular a sugestão.",
        },
      }));
    } finally {
      setBuscandoK((p) => ({ ...p, [config.id]: false }));
    }
  };

  const handleSalvar = async (configId) => {
    setSalvando((p) => ({ ...p, [configId]: true }));
    setErros((p) => ({ ...p, [configId]: null }));
    setSucessos((p) => ({ ...p, [configId]: null }));
    try {
      const dados = editando[configId];
      await atualizarConfiguracao(request, configId, {
        dias_em_aberto_max: Number(dados.dias_em_aberto_max),
        dias_quase_perdido_max: Number(dados.dias_quase_perdido_max),
        contagem: dados.contagem,
        alertas_ativos: dados.alertas_ativos,
        // Bloco E.2 v2 (ADR-0019)
        custo_operacional_padrao_percentual: dados.custo_operacional_padrao_percentual,
        margem_lucro_padrao_percentual: dados.margem_lucro_padrao_percentual,
        desconto_max_sem_aprovacao_percentual: dados.desconto_max_sem_aprovacao_percentual,
        meta_mensal_faturamento: dados.meta_mensal_faturamento ?? 0,
        alvo_tempo_resposta_horas: dados.alvo_tempo_resposta_horas,
        email_comercial: dados.email_comercial,
        telefone_comercial: dados.telefone_comercial,
        // Motor divisor (ADR-0050): "" vira null (nao configurado). O backend
        // exige k e L preenchidos quando motor_preco_padrao=DIVISOR.
        motor_preco_padrao: dados.motor_preco_padrao,
        k_cobertura_padrao_percentual:
          dados.k_cobertura_padrao_percentual === "" ? null : dados.k_cobertura_padrao_percentual,
        margem_liquida_alvo_padrao_percentual:
          dados.margem_liquida_alvo_padrao_percentual === "" ? null : dados.margem_liquida_alvo_padrao_percentual,
        imposto_venda_padrao_percentual:
          dados.imposto_venda_padrao_percentual === "" ? null : dados.imposto_venda_padrao_percentual,
        fatores_terceiro_padrao: dados.fatores_terceiro_padrao,
        // Dinheiro automatico (CRIT-6/CRIT-7): "" vira null = recurso desligado.
        lancar_comissao_automaticamente: Boolean(dados.lancar_comissao_automaticamente),
        categoria_lancamento_comissao: dados.categoria_lancamento_comissao || null,
        dias_apos_entrega_para_pagar_comissao: Number(
          dados.dias_apos_entrega_para_pagar_comissao || 0,
        ),
        categoria_lancamento_recebimento: dados.categoria_lancamento_recebimento || null,
        conta_recebimento_padrao: dados.conta_recebimento_padrao || null,
      });
      setSucessos((p) => ({ ...p, [configId]: "Configuração salva" }));
      // Auto-some apos 2.5s
      setTimeout(() => {
        setSucessos((p) => ({ ...p, [configId]: null }));
      }, 2500);
      await carregar();
    } catch (err) {
      const detalhe = err?.payload?.erros || err?.payload || {};
      const msg = typeof detalhe === "object" && Object.keys(detalhe).length
        ? Object.entries(detalhe).map(([k, v]) => `${k}: ${v}`).join(" | ")
        : err?.message || "Erro ao salvar";
      setErros((p) => ({ ...p, [configId]: msg }));
    } finally {
      setSalvando((p) => ({ ...p, [configId]: false }));
    }
  };

  return (
    <PageContainer>
      <EntityHeader
        title="Configurações comerciais"
        subtitle="Parâmetros de medição do funil de orçamentos por unidade"
      />

      <KpiStrip
        items={[
          { label: "Unidades configuradas", value: configs.length },
          {
            label: "Sua permissão",
            value: podeEditar ? "Pode editar" : "Só leitura",
            tone: podeEditar ? "success" : "neutral",
          },
        ]}
      />

      {loading ? (
        <EmptyMsg>Carregando configurações...</EmptyMsg>
      ) : configs.length === 0 ? (
        <EmptyMsg>
          Nenhuma unidade configurada. Aguarde o seed (data migration) ou crie
          uma BusinessUnit via admin Django.
        </EmptyMsg>
      ) : (
        configs.map((config) => {
          const local = editando[config.id] || {};
          const erro = erros[config.id];
          const sucesso = sucessos[config.id];
          return (
            <Card key={config.id}>
              <SectionTitle>
                {config.business_unit_codigo} — {config.business_unit_nome}
              </SectionTitle>

              <Form onSubmit={(e) => { e.preventDefault(); handleSalvar(config.id); }}>
                <FormGrid>
                  <Field>
                    Dias em aberto (max)
                    <Input
                      type="number" min="1" max="365"
                      value={local.dias_em_aberto_max ?? ""}
                      onChange={(e) => setCampo(config.id, "dias_em_aberto_max", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    />
                    <InlineHint>
                      0 a este número de dias desde envio = EM ABERTO (saudavel)
                    </InlineHint>
                  </Field>

                  <Field>
                    Dias quase perdido (max)
                    <Input
                      type="number" min="2" max="730"
                      value={local.dias_quase_perdido_max ?? ""}
                      onChange={(e) => setCampo(config.id, "dias_quase_perdido_max", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    />
                    <InlineHint>
                      Acima de 'dias em aberto' até este valor = QUASE PERDIDO; além = PERDIDO
                    </InlineHint>
                  </Field>

                  <Field>
                    Tipo de contagem
                    <Select
                      value={local.contagem ?? "CORRIDOS"}
                      onChange={(e) => setCampo(config.id, "contagem", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    >
                      <option value="CORRIDOS">Dias corridos</option>
                      <option value="UTEIS">Dias úteis (seg-sex)</option>
                    </Select>
                    <InlineHint>
                      Dias úteis ignora sábado/domingo (feriados ainda não tratados)
                    </InlineHint>
                  </Field>

                  <Field>
                    Alertas automaticos
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.88rem", color: "inherit" }}>
                      <input
                        type="checkbox"
                        checked={Boolean(local.alertas_ativos)}
                        onChange={(e) => setCampo(config.id, "alertas_ativos", e.target.checked)}
                        disabled={!podeEditar || salvando[config.id]}
                      />
                      Enviar email ao vendedor quando orçamento virar QUASE PERDIDO
                    </label>
                    <InlineHint>
                      Job diário executa o aviso. Idempotente: não reenvia.
                    </InlineHint>
                  </Field>

                  {/* Bloco E.2 v2 (ADR-0019) — parametros padrao da cascata de precificacao */}
                  <Field>
                    Custo operacional padrão (%)
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="500"
                      value={local.custo_operacional_padrao_percentual ?? "0"}
                      onChange={(e) => setCampo(config.id, "custo_operacional_padrao_percentual", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    />
                    <InlineHint>
                      Acrescimo aplicado sobre o custo CAD (% sobre soma dos materiais).
                    </InlineHint>
                  </Field>

                  <Field>
                    Margem de lucro padrão (%)
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="500"
                      value={local.margem_lucro_padrao_percentual ?? "30"}
                      onChange={(e) => setCampo(config.id, "margem_lucro_padrao_percentual", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    />
                    <InlineHint>
                      Margem aplicada sobre custo + operacional. Default 30%.
                    </InlineHint>
                  </Field>

                  <Field>
                    Desconto máx sem aprovação (%)
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={local.desconto_max_sem_aprovacao_percentual ?? "10"}
                      onChange={(e) => setCampo(config.id, "desconto_max_sem_aprovacao_percentual", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    />
                    <InlineHint>
                      Vendedor pode digitar desconto até este limite sem aprovação
                      especial. Acima exige a permissão de aprovar desconto.
                    </InlineHint>
                  </Field>

                  {/* Reteste 2026-08-18: a proposta imprimia o e-mail de
                      LOGIN do vendedor (o admin@ da marcenaria chegou
                      ao cliente). Credencial de acesso não é canal de
                      atendimento — o documento passa a usar este contato. */}
                  <Field>
                    E-mail comercial (aparece na proposta)
                    <Input
                      type="email"
                      value={local.email_comercial ?? ""}
                      onChange={(e) => setCampo(config.id, "email_comercial", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                      placeholder="vendas@suaempresa.com.br"
                    />
                    <InlineHint>
                      Sai na proposta, no PDF e no contrato. Em branco, o
                      documento não mostra e-mail — melhor do que mostrar o
                      login de alguém.
                    </InlineHint>
                  </Field>

                  <Field>
                    Telefone comercial (aparece na proposta)
                    <Input
                      type="text"
                      value={local.telefone_comercial ?? ""}
                      onChange={(e) => setCampo(config.id, "telefone_comercial", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                      placeholder="(11) 3000-0000"
                    />
                  </Field>
                </FormGrid>

                {/* ADR-0079: a meta tinha campo no banco e nenhuma tela; a
                    "meta do card" do painel era outra meta. Agora é uma só. */}
                <SectionTitle style={{ marginTop: 18 }}>
                  Painel comercial
                </SectionTitle>
                <FormGrid>
                  <Field>
                    Meta mensal de faturamento (R$)
                    <InputDinheiro
                      value={local.meta_mensal_faturamento}
                      onChange={(valor) => setCampo(config.id, "meta_mensal_faturamento", valor)}
                      disabled={!podeEditar || salvando[config.id]}
                    />
                    <InlineHint>
                      Usada na Receita realizada e no cabeçalho do painel. Em
                      Semana, Trimestre e Ano o painel usa a parte proporcional
                      (trimestre = 3 meses, ano = 12). Vazio ou zero = sem meta.
                    </InlineHint>
                  </Field>

                  <Field>
                    Alvo de tempo de resposta (h)
                    <Input
                      type="number"
                      step="0.5"
                      min="0.5"
                      max="999"
                      value={local.alvo_tempo_resposta_horas ?? "4"}
                      onChange={(e) => setCampo(config.id, "alvo_tempo_resposta_horas", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    />
                    <InlineHint>
                      Horas até o primeiro atendimento de um lead novo. O card
                      Tempo resposta compara com este alvo.
                    </InlineHint>
                  </Field>
                </FormGrid>

                {/* Auditoria 2026-08-07 (CRIT-6/CRIT-7): a seção que faltava.
                    Estas configurações decidem se a venda entregue vira
                    dinheiro no financeiro — sem elas, Contas a Receber e
                    Comissões ficam zerados para sempre. */}
                <SectionTitle style={{ marginTop: 18 }}>
                  Financeiro automático
                </SectionTitle>
                <InfoMsg style={{ marginBottom: 10 }}>
                  Enquanto a categoria de recebimento estiver vazia, o pedido
                  não gera contas a receber. Enquanto a comissão automática
                  estiver desligada, a entrega não lança comissão de parceiro.
                </InfoMsg>

                <FormGrid>
                  <Field>
                    <FieldLabel>Categoria de recebimento</FieldLabel>
                    <Select
                      value={local.categoria_lancamento_recebimento ?? ""}
                      onChange={(e) => setCampo(config.id, "categoria_lancamento_recebimento", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    >
                      <option value="">— desligado (não gerar) —</option>
                      {catsReceita.map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </Select>
                    <InlineHint>
                      Categoria (RECEITA) das parcelas previstas do pedido.
                      Preencher aqui é o que liga o Contas a Receber.
                    </InlineHint>
                  </Field>

                  <Field>
                    <FieldLabel>Conta de recebimento</FieldLabel>
                    <Select
                      value={local.conta_recebimento_padrao ?? ""}
                      onChange={(e) => setCampo(config.id, "conta_recebimento_padrao", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    >
                      <option value="">— primeira conta ativa —</option>
                      {contas.map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </Select>
                    <InlineHint>
                      Onde as parcelas entram. Com mais de uma conta, escolha
                      explicitamente — senão o sistema usa a primeira da lista.
                    </InlineHint>
                  </Field>

                  <Field>
                    <FieldLabel>Comissão automática na entrega</FieldLabel>
                    <label style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 0" }}>
                      <input
                        type="checkbox"
                        checked={Boolean(local.lancar_comissao_automaticamente)}
                        onChange={(e) => setCampo(config.id, "lancar_comissao_automaticamente", e.target.checked)}
                        disabled={!podeEditar || salvando[config.id]}
                      />
                      <span>Lançar comissão do parceiro quando o pedido é entregue</span>
                    </label>
                    <InlineHint>
                      Com entrega por ambiente, a comissão sai proporcional ao
                      que foi entregue e se completa na última entrega.
                    </InlineHint>
                  </Field>

                  <Field>
                    <FieldLabel>Categoria da comissão</FieldLabel>
                    <Select
                      value={local.categoria_lancamento_comissao ?? ""}
                      onChange={(e) => setCampo(config.id, "categoria_lancamento_comissao", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    >
                      <option value="">— criar/usar "Comissoes" —</option>
                      {catsDespesa.map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </Select>
                    <InlineHint>
                      Vazio: o sistema usa (ou cria) a categoria "Comissoes".
                    </InlineHint>
                  </Field>

                  <Field>
                    <FieldLabel>Dias após a entrega para pagar</FieldLabel>
                    <Input
                      type="number"
                      min="0"
                      value={local.dias_apos_entrega_para_pagar_comissao ?? 0}
                      onChange={(e) => setCampo(config.id, "dias_apos_entrega_para_pagar_comissao", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    />
                    <InlineHint>
                      Prazo entre a entrega e a competência da comissão. 0 = no
                      mesmo dia.
                    </InlineHint>
                  </Field>
                </FormGrid>

                {/* ADR-0050 — motor de preco divisor (kill-switch por unidade
                    + defaults k/L/imposto + fatores de terceiro) */}
                <SectionTitle style={{ marginTop: 18 }}>
                  Motor de preço — divisor (TOC)
                </SectionTitle>

                <FormGrid>
                  <Field>
                    Motor de preço padrão
                    <Select
                      value={local.motor_preco_padrao ?? "CASCATA"}
                      onChange={(e) => setCampo(config.id, "motor_preco_padrao", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                    >
                      <option value="CASCATA">Cascata (markup — legado)</option>
                      <option value="DIVISOR">Divisor (piso TOC)</option>
                    </Select>
                    <InlineHint>
                      Muda apenas orçamentos <strong>NOVOS</strong> — os
                      existentes mantêm o motor com que nasceram.
                    </InlineHint>
                  </Field>

                  <Field>
                    Cobertura de casa — k padrão (%)
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1000"
                      value={local.k_cobertura_padrao_percentual ?? ""}
                      onChange={(e) => setCampo(config.id, "k_cobertura_padrao_percentual", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                      placeholder="não configurado"
                    />
                    <SecondaryButton
                      type="button"
                      onClick={() => sugerirK(config)}
                      disabled={buscandoK[config.id]}
                      style={{ alignSelf: "flex-start", padding: "4px 10px", fontSize: "0.78rem" }}
                    >
                      {buscandoK[config.id] ? "Consultando..." : "Sugerir do financeiro"}
                    </SecondaryButton>
                    <InlineHint>
                      k = OE ÷ TVC: quanto cada real de material carrega de
                      despesa da casa. Obrigatório para usar o motor divisor.
                    </InlineHint>
                  </Field>

                  <Field>
                    Lucro líquido alvo — L padrão (%)
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="89"
                      value={local.margem_liquida_alvo_padrao_percentual ?? ""}
                      onChange={(e) => setCampo(config.id, "margem_liquida_alvo_padrao_percentual", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                      placeholder="não configurado"
                    />
                    <InlineHint>
                      Sobre o preço final (não é markup). Obrigatório para o
                      motor divisor; s + L acima de 90% bloqueia o cálculo.
                    </InlineHint>
                  </Field>

                  <Field>
                    Imposto de venda padrão (%)
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={local.imposto_venda_padrao_percentual ?? ""}
                      onChange={(e) => setCampo(config.id, "imposto_venda_padrao_percentual", e.target.value)}
                      disabled={!podeEditar || salvando[config.id]}
                      placeholder="não configurado"
                    />
                    <InlineHint>
                      Componente s3 do divisor — zerado no orçamento quando o
                      cliente não quer NF.
                    </InlineHint>
                  </Field>
                </FormGrid>

                {sugestoesK[config.id] && (
                  sugestoesK[config.id].erro ? (
                    <WarningMsg>{sugestoesK[config.id].erro}</WarningMsg>
                  ) : sugestoesK[config.id].k_sugerido_percentual == null ? (
                    <WarningMsg>
                      Sem sugestão: {sugestoesK[config.id].motivo
                        || "não há TVC lançado nos meses fechados da janela."}
                    </WarningMsg>
                  ) : (
                    <InfoMsg style={{ flexWrap: "wrap", gap: 10 }}>
                      <span style={{ flex: 1, minWidth: 240 }}>
                        Sugestão do financeiro:{" "}
                        <strong>
                          k = {Number(sugestoesK[config.id].k_sugerido_percentual).toFixed(2)}%
                        </strong>{" "}
                        — OE {fmtBRL(sugestoesK[config.id].oe)} ÷ TVC{" "}
                        {fmtBRL(sugestoesK[config.id].tvc)} no período{" "}
                        {sugestoesK[config.id].periodo_inicio} a{" "}
                        {sugestoesK[config.id].periodo_fim}
                        {sugestoesK[config.id].motivo
                          ? ` · ${sugestoesK[config.id].motivo}`
                          : ""}
                      </span>
                      {podeEditar && (
                        <SecondaryButton
                          type="button"
                          onClick={() => setCampo(
                            config.id,
                            "k_cobertura_padrao_percentual",
                            String(Number(sugestoesK[config.id].k_sugerido_percentual).toFixed(2)),
                          )}
                          style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                        >
                          Aplicar {Number(sugestoesK[config.id].k_sugerido_percentual).toFixed(2)}%
                        </SecondaryButton>
                      )}
                    </InfoMsg>
                  )
                )}

                <Field style={{ marginTop: 6 }}>
                  Fatores de terceiro padrão (por tipo de cotação)
                  <TableWrapper>
                    <Table>
                      <colgroup>
                        <col />
                        <col style={{ width: 140 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Fator (×)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {TIPOS_COTACAO.map(({ codigo, label }) => (
                          <tr key={codigo}>
                            <td>{label}</td>
                            <td>
                              <Input
                                type="number"
                                step="0.001"
                                min="1"
                                max="10"
                                style={{ width: 110 }}
                                value={
                                  (local.fatores_terceiro_padrao || {})[codigo]
                                  ?? FATOR_LUCRO_PADRAO_POR_TIPO[codigo]
                                  ?? "1.500"
                                }
                                onChange={(e) => setCampo(
                                  config.id,
                                  "fatores_terceiro_padrao",
                                  {
                                    ...(local.fatores_terceiro_padrao || {}),
                                    [codigo]: e.target.value,
                                  },
                                )}
                                disabled={!podeEditar || salvando[config.id]}
                                aria-label={`Fator padrão de ${label}`}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrapper>
                  <InlineHint>
                    Multiplicador aplicado sobre o valor da cotação{" "}
                    <strong>antes</strong> da divisão (1 a 10). O vendedor pode
                    ajustar por cotação.
                  </InlineHint>
                </Field>

                <FieldRow>
                  <FieldLabel>Exemplo</FieldLabel>
                  <FieldValue>
                    Com estes valores: enviado ha <strong>{local.dias_em_aberto_max || 30} dias</strong> = EM_ABERTO ·
                    {" "}<strong>{Number(local.dias_em_aberto_max || 30) + 1} a {local.dias_quase_perdido_max || 60} dias</strong> = QUASE_PERDIDO ·
                    {" "}<strong>{Number(local.dias_quase_perdido_max || 60) + 1}+ dias</strong> = PERDIDO
                  </FieldValue>
                </FieldRow>

                {erro && (
                  <ErrorMsg>
                    <FiAlertCircle size={14} aria-hidden="true" /> {erro}
                  </ErrorMsg>
                )}

                {sucesso && (
                  <div
                    role="status"
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      background: "var(--success-bg)",
                      color: "var(--success-text)",
                      fontSize: "0.85rem",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      alignSelf: "flex-start",
                    }}
                  >
                    <FiCheck size={14} /> {sucesso}
                  </div>
                )}

                {podeEditar && (
                  <FormFooter>
                    <PrimaryButton type="submit" disabled={salvando[config.id]}>
                      <FiSave size={14} />
                      {salvando[config.id] ? "Salvando..." : "Salvar"}
                    </PrimaryButton>
                  </FormFooter>
                )}
              </Form>
            </Card>
          );
        })
      )}

      {/* Tarefa F2: formas de pagamento (taxa s2 do motor + cronograma de
          recebimento do pedido por evento do processo). */}
      {!loading && (
        <FormasPagamentoSection podeEditar={podeEditar} />
      )}
    </PageContainer>
  );
}

export default ConfiguracoesComercial;
